import type { FC, HTMLAttributes, KeyboardEvent } from 'react'
import styles from './button-group.module.css'

export interface ButtonGroupProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'text' | 'contained' | 'outlined'
  color?: 'primary' | 'secondary' | 'error' | 'inherit'
  size?: 'small' | 'medium' | 'large'
  orientation?: 'horizontal' | 'vertical'
}

export const ButtonGroup: FC<ButtonGroupProps> = ({
  orientation = 'horizontal',
  className,
  onKeyDown,
  ...rest
}) => {
  const classes = [
    styles.buttonGroup,
    orientation === 'vertical' ? styles.vertical : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(e)
    const isHorizontal = orientation !== 'vertical'
    const nextKey = isHorizontal ? 'ArrowRight' : 'ArrowDown'
    const prevKey = isHorizontal ? 'ArrowLeft' : 'ArrowUp'

    if (e.key !== nextKey && e.key !== prevKey) {return}

    const buttons = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'))
    const idx = buttons.indexOf(e.target as HTMLButtonElement)
    if (idx === -1) {return}

    e.preventDefault()
    const next = e.key === nextKey
      ? buttons[(idx + 1) % buttons.length]
      : buttons[(idx - 1 + buttons.length) % buttons.length]
    next?.focus()
  }

  return (
    <div
      className={classes}
      role="group"
      onKeyDown={handleKeyDown}
      {...rest}
    />
  )
}
